/**
 * Shared Zod fragments. Architecture §15 rule 3: end-to-end Zod.
 */
import { z } from "zod";

/** Money is integer paise as a stringified bigint (JSON-safe). */
export const moneyPaise = z.string().regex(/^\d+$/, "paise must be a non-negative integer string");

/**
 * Same wire shape as `moneyPaise`, but the leading `-` is allowed for
 * fields that carry a signed running balance, a variance, or a delta.
 *
 * Use `signedPaise` ONLY for derived/computed values where negative is
 * meaningful (e.g. drawer balance after an over-balance post, count
 * variance, after-transfer delta). Stored money fields — invoice totals,
 * voucher amounts, etc. — always use `moneyPaise`.
 */
export const signedPaise = z.string().regex(/^-?\d+$/, "paise must be an integer string (signed)");

export const uuid = z.string().uuid();

export const phoneIN = z.string().regex(/^[6-9]\d{9}$/, "invalid Indian mobile");

export const gstin = z
  .string()
  .regex(/^[0-9]{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/, "invalid GSTIN");

export const pin = z.string().regex(/^[1-9][0-9]{5}$/, "invalid PIN code");

/**
 * RBI IFSC code — exactly 11 chars: 4-letter bank code + `0` + 6 alphanumeric
 * branch code. Used by `ifsc.lookup` (auto-fill from Razorpay) and by every
 * bank-detail mutation (vendor change-bank, bank-account create).
 */
export const ifsc = z
  .string()
  .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "invalid IFSC (format: AAAA0XXXXXX)");

/**
 * Indian state / UT enum — drives intra-state vs inter-state GST defaults.
 * The first two GSTIN characters are the state code; this enum carries the
 * human label that appears on UI and on the company row.
 */
export const indianState = z.enum([
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
]);

/**
 * ISO 3166-1 short-form country names.
 *
 * A closed set rather than free text: the value goes on export paperwork, so
 * "asdf" and "Bharat" and "INDIA" all being storable is a real problem, not a
 * tidiness one. Shaped like {@link indianState} — human labels, not codes —
 * because that is what renders on the form and on a document, and the
 * alpha-2 code can be derived later if customs integration needs it.
 *
 * Names are the UN/ISO short form, so an operator searching the picker types
 * what they'd write on an invoice. Consumed as
 * `country.options.map((c) => ({ value: c, label: c }))`.
 */
export const country = z.enum([
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei Darussalam",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czechia",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Lao People's Democratic Republic",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Republic of Korea",
  "Romania",
  "Russian Federation",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syrian Arab Republic",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Türkiye",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States of America",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Viet Nam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
]);

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD required");

/**
 * Refinement: `dueOn` field must be ≥ `issuedOn`. String compare works because
 * both are validated `YYYY-MM-DD` (lexicographic == chronological). Used by
 * `invoices.createDraft / updateDraft / issue` so an operator can't save a
 * draft due before it's issued. Domain ops re-check at the boundary in case
 * a non-tRPC caller skips the contract.
 */
export const requireDueOnAfterIssuedOn = <T extends { issuedOn: string; dueOn: string }>(
  schema: z.ZodType<T>,
): z.ZodEffects<z.ZodType<T>, T, T> =>
  schema.refine((d) => d.dueOn >= d.issuedOn, {
    path: ["dueOn"],
    message: "Due date must be on or after the issue date.",
  });

export const cursor = z.string().optional();

export const paged = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: cursor,
  });

/**
 * Petty-cash physical count — pieces per RBI denomination.
 *
 * Each field is a non-negative integer count of pieces of that
 * denomination. Stored on the audit metadata of a count post (either
 * `bank_accounts.markCounted` for zero-variance OR `cashMovements.post`
 * with `kind=cash_count_adjustment` for variance) so the counts-history
 * surface can show "₹500 × N + ₹200 × M + …" reconciliation.
 *
 * Keys mirror RBI denominations: `d500` = ₹500 notes, `d1` = ₹1 coin.
 * Default 0 so partial counts (only-banknotes, only-coins) round-trip
 * without the SPA filling zeros explicitly.
 *
 * @see packages/domain/src/money/bank-accounts/mark-counted.ts
 * @see packages/domain/src/money/cash-movements/post-cash-movement.ts
 */
export const denominationCount = z.object({
  d500: z.number().int().min(0).default(0),
  d200: z.number().int().min(0).default(0),
  d100: z.number().int().min(0).default(0),
  d50: z.number().int().min(0).default(0),
  d20: z.number().int().min(0).default(0),
  d10: z.number().int().min(0).default(0),
  d5: z.number().int().min(0).default(0),
  d2: z.number().int().min(0).default(0),
  d1: z.number().int().min(0).default(0),
});
export type DenominationCount = z.infer<typeof denominationCount>;
